import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Copy, Eye, FlaskConical, Gauge, ListChecks, Pencil, Play, Plus, ShieldCheck, Trash2, UserMinus, UserPlus, Users } from '@/components/icons';
import { BROWSERS, buildsSupported, type Browser } from '@1qa/shared';
import { BuildsTab } from '@/components/builds/BuildsTab';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { URL_HELP, URL_RULE_MESSAGE, isValidUrl, timeAgo } from '@/lib/format';
import type { Project, Run, TestCase, TraceMode, VideoMode } from '@/lib/types';
import { TestDataWithCollections } from '@/components/testdata/TestDataCollections';
import { PLATFORM_META, PlatformIcon } from '@/components/PlatformIcon';
import { RunnerWarning } from '@/components/RunnerWarning';
import { DeletableRunsTable } from '@/components/report/DeletableRunsTable';
import { OwnerFilter } from '@/components/OwnerFilter';
import { NewTestChooser } from '@/components/NewTestChooser';
import { LoadLimitsCard } from '@/components/perf/LoadLimitsCard';
import { ServerCertsCard } from '@/components/perf/ServerCertsCard';
import { ScriptsTab } from '@/components/perf/ScriptsTab';
import { PerfTestsTab } from '@/components/perf/PerfTestsTab';
import { SecurityTestsTab } from '@/components/security/SecurityTestsTab';
import { CodeRepoCard } from '@/components/security/CodeRepoPanel';
import { GroupsTab } from '@/components/groups/GroupsTab';
import { FlowsTab } from '@/components/flows/FlowsTab';
import { AutomationTab } from '@/components/triggers/AutomationTab';
import { filterByOwner, useOwnerMode } from '@/lib/owner';
import { WatchLiveToggle } from '@/components/report/WatchLiveToggle';
import { liveToggleState, useWatchLivePreference } from '@/lib/liveView';
import { useRunCollection } from '@/lib/runCollection';
import { RunCollectionPicker } from '@/components/testdata/RunCollectionPicker';
import { useRunBuild } from '@/lib/runBuild';
import { RunBuildPicker } from '@/components/builds/RunBuildPicker';
import { useRunDevice } from '@/lib/runDevice';
import { RunDevicePicker } from '@/components/builds/RunDevicePicker';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  LinkButton,
  PageHeader,
  PageLoader,
  Select,
  TabPanel,
  Tabs,
  Textarea,
  useConfirm,
  useToast,
} from '@/components/ui';

type Tab = 'tests' | 'scripts' | 'groups' | 'flows' | 'automation' | 'runs' | 'builds' | 'data' | 'team' | 'settings';
/** Functional tests open in the builder, performance tests on their own page. */
const testHref = (projectId: string, t: Pick<TestCase, 'id' | 'type'>) =>
  t.type === 'PERFORMANCE' ? `/projects/${projectId}/perf/${t.id}` : t.type === 'SECURITY' ? `/projects/${projectId}/security/${t.id}` : `/projects/${projectId}/tests/${t.id}`;

const TABS: Tab[] = ['tests', 'groups', 'flows', 'automation', 'runs', 'builds', 'data', 'team', 'settings'];
const PERF_TABS: Tab[] = ['tests', 'scripts', 'runs', 'data', 'team', 'settings'];
const PERF_TAB_LABEL: Partial<Record<Tab, string>> = { tests: 'Performance tests', scripts: 'Scripts', runs: 'Runs', data: 'Test data', team: 'Team', settings: 'Settings' };
const SEC_TABS: Tab[] = ['tests', 'runs', 'data', 'team', 'settings'];
const SEC_TAB_LABEL: Partial<Record<Tab, string>> = { tests: 'Security tests', runs: 'Runs', data: 'Test data', team: 'Team', settings: 'Settings' };

const VIDEO_OPTIONS: { value: VideoMode; label: string }[] = [
  { value: 'off', label: 'Off' },
  { value: 'retain-on-failure', label: 'Keep for failed tests' },
  { value: 'on', label: 'Always' },
];
const TRACE_OPTIONS: { value: TraceMode; label: string }[] = [
  { value: 'on', label: 'Always' },
  { value: 'retain-on-failure', label: 'Only for failed tests' },
];
const DEFAULT_STEP_RETRIES = 2; // mirrors the runner default
const STEP_RETRY_OPTIONS = [
  { value: '0', label: 'Off - fail at once' },
  { value: '1', label: 'Once' },
  { value: '2', label: 'Twice (recommended)' },
  { value: '3', label: '3 times' },
];

function TestDataTab({ project, canEdit }: { project: Project; canEdit: boolean }) {
  const vars = useQuery({
    queryKey: ['project-variables', project.id],
    queryFn: () => api.projectVariables(project.id),
  });
  if (vars.isPending) return <PageLoader />;
  if (vars.isError) return <ErrorNotice error={vars.error} onRetry={() => vars.refetch()} />;
  return (
    <div className="space-y-6">
      <TestDataWithCollections
        scope="PROJECT"
        orgId={project.orgId}
        projectId={project.id}
        variables={vars.data}
        canEdit={canEdit}
        canCreateCollections={canEdit}
        createVariable={(input) => api.createProjectVariable(project.id, input)}
        variableQueryKeys={[['project-variables', project.id]]}
        sharedDescription="Values your tests can use in every environment, written as {{NAME}} in a step. Organization values apply to all projects; a project value with the same name wins. A collection (e.g. Staging) picked for a run replaces the values it also has."
      />
    </div>
  );
}

function TestsTab({ project, tests, canWrite, canRun }: { project: Project; tests: TestCase[]; canWrite: boolean; canRun: boolean }) {
  const { user, canModify } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [choosing, setChoosing] = useState(false);
  const [owner, setOwnerState] = useOwnerMode();
  const myTests = filterByOwner(tests, (t) => t.createdById, user?.id, 'mine');
  const shown = owner === 'mine' ? myTests : tests;
  // Selection only covers tests on screen, so switching the filter clears it.
  const setOwner = (mode: typeof owner) => {
    setSelected(new Set());
    setOwnerState(mode);
  };
  const [watchLive, setWatchLive] = useWatchLivePreference();
  const live = liveToggleState(project, watchLive);
  const runData = useRunCollection(project.id);
  const runBuild = useRunBuild(project.id, project.platform);
  const runDevice = useRunDevice(project.id, project.platform);

  const run = useMutation({
    mutationFn: (ids?: string[]) =>
      api.triggerRun(project.id, ids, { liveView: live.checked, collectionId: runData.collectionId, appBuildId: runBuild.appBuildId, deviceProfile: runDevice.deviceProfile }),
    onSuccess: (r) => {
      toast.success('Run started');
      qc.invalidateQueries({ queryKey: ['runs', project.id] });
      navigate(`/runs/${r.id}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteTest(id),
    onSuccess: () => {
      toast.success('Test deleted');
      qc.invalidateQueries({ queryKey: ['tests', project.id] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const copy = useMutation({
    mutationFn: (id: string) => api.copyTest(id),
    onSuccess: (t) => {
      toast.success(`“${t.name}” created - it's yours to edit`);
      qc.invalidateQueries({ queryKey: ['tests', project.id] });
      navigate(testHref(project.id, t));
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const chooser = canWrite && <NewTestChooser project={project} open={choosing} onClose={() => setChoosing(false)} />;

  if (tests.length === 0) {
    return (
      <>
        {chooser}
        <EmptyState
          icon={<FlaskConical className="size-6" />}
          title="No tests yet"
          description="Record your first test in the browser, or describe it in plain English - for example “open the login page, sign in, check the dashboard”."
          action={
            canWrite && (
              <Button onClick={() => setChoosing(true)} icon={<Plus className="size-4" />}>
                Create a test
              </Button>
            )
          }
        />
      </>
    );
  }

  return (
    <Card>
      {chooser}
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-3.5 dark:border-slate-800">
        <OwnerFilter value={owner} onChange={setOwner} mineCount={myTests.length} allCount={tests.length} mineLabel="My tests" />
        {canRun && shown.length > 0 && (
          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <input
              type="checkbox"
              className="size-4 accent-primary"
              checked={selected.size === shown.length}
              onChange={(e) => setSelected(e.target.checked ? new Set(shown.map((t) => t.id)) : new Set())}
              aria-label="Select all tests"
            />
            {selected.size ? `${selected.size} selected` : 'Select tests'}
          </label>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-3">
          {canRun && <RunBuildPicker state={runBuild} />}
          {canRun && <RunDevicePicker state={runDevice} />}
          {canRun && <RunCollectionPicker state={runData} />}
          {canRun && <WatchLiveToggle state={live} onChange={setWatchLive} />}
          {/* "My tests" with nothing selected has no bulk run button: select tests, or switch to Everyone for "Run all tests". */}
          {canRun && (selected.size > 0 || owner === 'all') && (
            <Button
              variant={selected.size ? 'primary' : 'secondary'}
              onClick={() => run.mutate(selected.size ? [...selected] : undefined)}
              disabled={!selected.size && shown.length === 0}
              loading={run.isPending}
              icon={<Play className="size-4" />}
            >
              {selected.size ? `Run ${selected.size} selected` : 'Run all tests'}
            </Button>
          )}
          {canWrite && (
            <Button variant="outline" onClick={() => setChoosing(true)} icon={<Plus className="size-4" />}>
              New test
            </Button>
          )}
        </div>
      </div>
      {shown.length === 0 && (
        <div className="px-5 py-10 text-center text-sm text-slate-500">
          <p>You haven't created any tests in this project yet.</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => setOwner('all')}>
            Show everyone's tests ({tests.length})
          </Button>
        </div>
      )}
      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
        {shown.map((t) => {
          const mine = canModify('test.write', t.createdById, project.orgId);
          // Performance tests and security scans have their own page and run on their own.
          const perf = t.type === 'PERFORMANCE';
          const security = t.type === 'SECURITY';
          const standalone = perf || security;
          const href = testHref(project.id, t);
          return (
            <li key={t.id} className="group flex items-center gap-4 px-5 py-3.5">
              {canRun && standalone && <span className="size-4" aria-hidden />}
              {canRun && !standalone && (
                <input
                  type="checkbox"
                  className="size-4 accent-primary"
                  checked={selected.has(t.id)}
                  onChange={() => toggle(t.id)}
                  aria-label={`Select ${t.name}`}
                />
              )}
              <span className="grid size-9 place-items-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-900/40 dark:text-brand-300">
                {perf ? <Gauge className="size-[18px]" /> : security ? <ShieldCheck className="size-[18px]" /> : <ListChecks className="size-[18px]" />}
              </span>
              <Link to={href} className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-slate-900 hover:text-brand-600 dark:text-white">{t.name}</div>
                <div className="truncate text-xs text-slate-500">
                  {perf
                    ? `Performance test · ${t.perfConfig?.scenarios?.length ?? 0} scenario${t.perfConfig?.scenarios?.length === 1 ? '' : 's'}`
                    : security
                      ? 'Security scan'
                      : `${t.steps.length} step${t.steps.length === 1 ? '' : 's'}`}{' '}
                  · updated {timeAgo(t.updatedAt)}
                  {t.createdBy && ` · by ${t.createdBy.name}`}
                </div>
              </Link>
              <div className="hidden gap-1 md:flex">
                {t.tags.slice(0, 3).map((tag) => (
                  <Badge key={tag}>{tag}</Badge>
                ))}
              </div>
              <div className="flex gap-1 opacity-100 transition sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100">
                {canRun && (
                  <Button size="sm" variant="ghost" aria-label={`Run ${t.name}`} onClick={() => (standalone ? navigate(`${href}?run=1`) : run.mutate([t.id]))} icon={<Play className="size-4" />} />
                )}
                {canWrite && (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Make a copy of ${t.name}`}
                    title="Make a copy you can edit"
                    loading={copy.isPending && copy.variables === t.id}
                    onClick={() => copy.mutate(t.id)}
                    icon={<Copy className="size-4" />}
                  />
                )}
                {!mine && (
                  <LinkButton
                    size="sm"
                    variant="ghost"
                    to={href}
                    aria-label={`View ${t.name}`}
                    icon={<Eye className="size-4" />}
                  />
                )}
                {mine && (
                  <>
                    <LinkButton
                      size="sm"
                      variant="ghost"
                      to={href}
                      aria-label={`Edit ${t.name}`}
                      icon={<Pencil className="size-4" />}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Delete ${t.name}`}
                      onClick={async () => {
                        const ok = await confirm({
                          title: 'Delete this test?',
                          message: `“${t.name}” and its results in past reports will be deleted. This cannot be undone.`,
                          confirmLabel: 'Delete test',
                        });
                        if (ok) remove.mutate(t.id);
                      }}
                      icon={<Trash2 className="size-4 text-rose-500" />}
                    />
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/** Project Members assigned to this project; Project Admins add or remove them here. */
function TeamTab({ project }: { project: Project }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const canManage = can('project.members.manage', project.orgId);
  const team = useQuery({
    queryKey: ['project-members', project.id],
    queryFn: () => api.projectMembers(project.id),
  });
  const orgMembers = useQuery({
    queryKey: ['members', project.orgId],
    queryFn: () => api.members(project.orgId),
    enabled: canManage,
  });
  const [adding, setAdding] = useState('');

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['project-members', project.id] });
    qc.invalidateQueries({ queryKey: ['members', project.orgId] });
  };
  const add = useMutation({
    mutationFn: (userId: string) => api.assignProjectMember(project.id, userId),
    onSuccess: () => {
      toast.success('Member added to the project');
      setAdding('');
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (userId: string) => api.unassignProjectMember(project.id, userId),
    onSuccess: () => {
      toast.success('Member removed from the project');
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (team.isPending) return <PageLoader />;
  if (team.isError) return <ErrorNotice error={team.error} onRetry={() => team.refetch()} />;
  const assignedIds = new Set(team.data.map((m) => m.userId));
  const candidates = (orgMembers.data ?? []).filter((m) => m.role === 'MEMBER' && !assignedIds.has(m.userId));

  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Users className="size-4 text-brand-500" /> Project team
          </span>
        }
        description="Project Members listed here can see everything in this project and run its tests; they edit only the tests and test data they create. Project Admins always have access."
      />
      {canManage && (
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-800">
          <Select
            aria-label="Project Member to add"
            wrapperClassName="w-72"
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            options={[
              {
                value: '',
                label: candidates.length ? 'Choose a Project Member…' : 'Every Project Member is already here',
              },
              ...candidates.map((m) => ({
                value: m.userId,
                label: `${m.name} (${m.email})`,
              })),
            ]}
          />
          <Button onClick={() => adding && add.mutate(adding)} disabled={!adding} loading={add.isPending} icon={<UserPlus className="size-4" />}>
            Add to project
          </Button>
          <LinkButton to={`/members?invite=1&project=${encodeURIComponent(project.id)}`} variant="ghost">
            Invite someone new
          </LinkButton>
        </div>
      )}
      {team.data.length === 0 ? (
        <div className="p-5">
          <EmptyState
            icon={<Users className="size-6" />}
            title="No Project Members yet"
            description={canManage ? 'Add Project Members so they can work on this project.' : 'Only Project Admins work on this project so far.'}
          />
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {team.data.map((m) => (
            <li key={m.userId} className="flex items-center gap-4 px-5 py-3.5">
              <span className="grid size-9 place-items-center rounded-full bg-slate-100 text-sm font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {m.name.charAt(0).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{m.name}</div>
                <div className="truncate text-xs text-slate-500">
                  {m.email} · added {timeAgo(m.assignedAt)}
                </div>
              </div>
              {m.status === 'DISABLED' && <Badge tone="warning">Disabled</Badge>}
              {canManage && (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Remove ${m.name} from the project`}
                  loading={remove.isPending && remove.variables === m.userId}
                  onClick={() => remove.mutate(m.userId)}
                  icon={<UserMinus className="size-4 text-rose-500" />}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function RunsTab({ projectId, orgId }: { projectId: string; orgId: string }) {
  const { user, canModify } = useAuth();
  const [owner, setOwner] = useOwnerMode();
  // Only the person who started a run (or a Project Admin) deletes its report.
  const canDeleteRun = useCallback((r: Run) => canModify('report.delete', r.triggeredBy?.id, orgId), [canModify, orgId]);
  const runs = useQuery({
    queryKey: ['runs', projectId],
    queryFn: () => api.projectRuns(projectId),
    refetchInterval: (q) => (q.state.data?.some((r) => r.status === 'QUEUED' || r.status === 'RUNNING') ? 3000 : false),
  });
  if (runs.isPending) return <PageLoader />;
  if (runs.isError) return <ErrorNotice error={runs.error} onRetry={() => runs.refetch()} />;
  if (runs.data.length === 0) return <EmptyState title="No runs yet" description="Press “Run all tests” to start your first run." />;
  const myRuns = filterByOwner(runs.data, (r) => r.triggeredBy?.id, user?.id, 'mine');
  const shown = owner === 'mine' ? myRuns : runs.data;
  return (
    <Card>
      <div className="border-b border-slate-100 px-5 py-3.5 dark:border-slate-800">
        <OwnerFilter value={owner} onChange={setOwner} mineCount={myRuns.length} allCount={runs.data.length} mineLabel="Started by me" />
      </div>
      {shown.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-slate-500">
          <p>You haven't started any runs in this project yet.</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => setOwner('all')}>
            Show everyone's runs ({runs.data.length})
          </Button>
        </div>
      ) : (
        <DeletableRunsTable runs={shown} showProject={false} canDelete={canDeleteRun} />
      )}
    </Card>
  );
}

function SettingsTab({ project, canEdit, canDelete }: { project: Project; canEdit: boolean; canDelete: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description ?? '');
  const [targetUrl, setTargetUrl] = useState(project.targetUrl ?? '');
  const [appId, setAppId] = useState(project.appId ?? '');
  const [browser, setBrowser] = useState<Browser>((project.settings?.browser as Browser) ?? 'chromium');
  const [video, setVideo] = useState<VideoMode>(project.settings?.video ?? 'retain-on-failure');
  const [trace, setTrace] = useState<TraceMode>(project.settings?.trace ?? 'on');
  const [stepRetries, setStepRetries] = useState(project.settings?.stepRetries ?? DEFAULT_STEP_RETRIES);

  useEffect(() => {
    setName(project.name);
    setDescription(project.description ?? '');
    setTargetUrl(project.targetUrl ?? '');
    setAppId(project.appId ?? '');
    setVideo(project.settings?.video ?? 'retain-on-failure');
    setTrace(project.settings?.trace ?? 'on');
    setStepRetries(project.settings?.stepRetries ?? DEFAULT_STEP_RETRIES);
  }, [project]);

  const save = useMutation({
    mutationFn: () =>
      api.updateProject(project.id, {
        name: name.trim(),
        description: description.trim(),
        ...(project.platform === 'PERFORMANCE'
          ? { targetUrl: targetUrl.trim() }
          : project.platform === 'SECURITY'
          ? // The code repository has its own connection (Code repository card below).
            { targetUrl: targetUrl.trim() }
          : project.platform === 'WEB'
          ? {
              targetUrl: targetUrl.trim(),
              settings: {
                ...project.settings,
                browser,
                video,
                trace,
                stepRetries,
              },
            }
          : {
              appId: appId.trim(),
              settings: { ...project.settings, video, trace, stepRetries },
            }),
      }),
    onSuccess: () => {
      toast.success('Settings saved');
      qc.invalidateQueries({ queryKey: ['project', project.id] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const del = useMutation({
    mutationFn: () => api.deleteProject(project.id),
    onSuccess: () => {
      toast.success('Project deleted');
      qc.invalidateQueries({ queryKey: ['projects'] });
      navigate('/projects', { replace: true });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  const isPerf = project.platform === 'PERFORMANCE';
  const isSec = project.platform === 'SECURITY';
  const urlInvalid = project.platform === 'WEB' || isSec ? !isValidUrl(targetUrl.trim()) : isPerf && !/^https?:\/\/[^/\s]+/i.test(targetUrl.trim());

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <Card className="lg:col-span-2">
        <CardHeader title="Project settings" />
        <CardBody>
          <form onSubmit={onSubmit} className="space-y-5">
            <fieldset disabled={!canEdit} className="space-y-5">
              <Field label="Name">{(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
              <Field label="Description">{(id) => <Textarea id={id} value={description} onChange={(e) => setDescription(e.target.value)} />}</Field>
              {isPerf ? (
                <Field label="Address of the app to load" hint="Performance tests put load on this host (and the hosts allowed in the load limits)." error={urlInvalid ? 'An http:// or https:// address' : undefined}>
                  {(id) => <Input id={id} type="url" placeholder="https://" value={targetUrl} invalid={urlInvalid} onChange={(e) => setTargetUrl(e.target.value)} />}
                </Field>
              ) : isSec ? (
                <>
                  <Field label="Address of the app to test" hint="The app the pentester scans. Confirm the authorized hosts (scope) on each scan before running." error={urlInvalid ? URL_RULE_MESSAGE : undefined}>
                    {(id) => <Input id={id} type="url" placeholder="https://" value={targetUrl} invalid={urlInvalid} onChange={(e) => setTargetUrl(e.target.value)} />}
                  </Field>
                </>
              ) : project.platform === 'WEB' ? (
                <>
                  <Field label="Website address" hint={URL_HELP} error={urlInvalid ? URL_RULE_MESSAGE : undefined}>
                    {(id) => (
                      <Input id={id} type="url" placeholder="https://" value={targetUrl} invalid={urlInvalid} onChange={(e) => setTargetUrl(e.target.value)} />
                    )}
                  </Field>
                  <Field label="Browser">
                    {(id) => (
                      <Select
                        id={id}
                        value={browser}
                        onChange={(e) => setBrowser(e.target.value as Browser)}
                        options={BROWSERS.map((b) => ({ value: b, label: b }))}
                      />
                    )}
                  </Field>
                </>
              ) : (
                <Field label="App identifier" hint={project.appPath ? `Uploaded build: ${project.appPath}` : 'No app build uploaded yet.'}>
                  {(id) => <Input id={id} value={appId} onChange={(e) => setAppId(e.target.value)} />}
                </Field>
              )}
              {!isPerf && !isSec && (
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Video recording" hint="Videos show exactly what happened, but take more storage.">
                  {(id) => <Select id={id} value={video} onChange={(e) => setVideo(e.target.value as VideoMode)} options={VIDEO_OPTIONS} />}
                </Field>
                <Field label="Step trace" hint="A step-by-step record with screenshots of every check.">
                  {(id) => <Select id={id} value={trace} onChange={(e) => setTrace(e.target.value as TraceMode)} options={TRACE_OPTIONS} />}
                </Field>
                <Field
                  label="Retry failed steps"
                  hint="When a step fails because the page was still loading or a check could not decide, 1QA waits a moment and tries that step again, without starting the test over."
                >
                  {(id) => <Select id={id} value={String(stepRetries)} onChange={(e) => setStepRetries(Number(e.target.value))} options={STEP_RETRY_OPTIONS} />}
                </Field>
              </div>
              )}
            </fieldset>
            {canEdit && (
              <div className="flex justify-end">
                <Button type="submit" loading={save.isPending} disabled={!name.trim() || urlInvalid}>
                  Save changes
                </Button>
              </div>
            )}
          </form>
        </CardBody>
      </Card>
      {canDelete && (
        <Card className="h-fit ring-rose-200 dark:ring-rose-900/60">
          <CardHeader title="Danger zone" description="Deleting removes all tests and reports of this project." />
          <CardBody>
            <Button
              variant="danger"
              loading={del.isPending}
              icon={<Trash2 className="size-4" />}
              onClick={async () => {
                const ok = await confirm({
                  title: 'Delete this project?',
                  message: `“${project.name}” and all of its tests, reports, screenshots and videos will be deleted. This cannot be undone.`,
                  confirmLabel: 'Delete project',
                });
                if (ok) del.mutate();
              }}
            >
              Delete project
            </Button>
          </CardBody>
        </Card>
      )}
      {isPerf && <LoadLimitsCard projectId={project.id} canEdit={canEdit} />}
      {isPerf && <ServerCertsCard projectId={project.id} canEdit={canEdit} />}
      {isSec && (
        <div className="lg:col-span-2">
          <CodeRepoCard projectId={project.id} canManage={canEdit} />
        </div>
      )}
    </div>
  );
}

export function ProjectDetailPage() {
  const { projectId = '' } = useParams();
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const wanted = params.get('tab') as Tab;

  const project = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.project(projectId),
  });
  // Also refetched on returning to the tab: tests can be added elsewhere (e.g. saved from the recorder).
  const tests = useQuery({ queryKey: ['tests', projectId], queryFn: () => api.tests(projectId), refetchOnWindowFocus: true });

  if (project.isPending) return <PageLoader />;
  if (project.isError) return <ErrorNotice error={project.error} onRetry={() => project.refetch()} />;

  const p = project.data;
  const orgId = p.orgId;
  // Performance projects: performance tests, scripts, runs, test data, team, settings (with load limits).
  const isPerf = p.platform === 'PERFORMANCE';
  // Security projects: security tests (scan configs), runs, test data, team, settings (with scope).
  const isSec = p.platform === 'SECURITY';
  const tabSet = isPerf ? PERF_TABS : isSec ? SEC_TABS : TABS;
  const tab = (tabSet.includes(wanted) ? wanted : 'tests') as Tab;
  const canWrite = can('test.write', orgId);
  const canRun = can('run.trigger', orgId);

  return (
    <div className="space-y-6">
      <Link to="/projects" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">
        <ArrowLeft className="size-4" /> All projects
      </Link>
      <PageHeader
        eyebrow={PLATFORM_META[p.platform].label}
        title={
          <span className="flex items-center gap-4">
            <PlatformIcon platform={p.platform} size="lg" />
            {p.name}
          </span>
        }
        description={p.description || (p.platform === 'WEB' || isPerf || isSec ? p.targetUrl : p.appId)}
      />
      <RunnerWarning platform={p.platform} />
      <Tabs<Tab>
        label="Project sections"
        value={tab}
        onChange={(t) =>
          setParams(
            (prev) => {
              const n = new URLSearchParams(prev);
              if (t === 'tests') n.delete('tab');
              else n.set('tab', t);
              return n;
            },
            { replace: true },
          )
        }
        items={
          isPerf
            ? PERF_TABS.map((id) => ({ id, label: PERF_TAB_LABEL[id], ...(id === 'tests' ? { count: tests.data?.length } : {}) }))
            : isSec
              ? SEC_TABS.map((id) => ({ id, label: SEC_TAB_LABEL[id], ...(id === 'tests' ? { count: tests.data?.length } : {}) }))
              : [
                  { id: 'tests', label: 'Tests', count: tests.data?.length },
                  { id: 'groups', label: 'Groups' },
                  { id: 'flows', label: 'Flows' },
                  { id: 'automation', label: 'Automation' },
                  { id: 'runs', label: 'Runs' },
                  ...(buildsSupported(p.platform) ? [{ id: 'builds' as const, label: 'Builds' }] : []),
                  { id: 'data', label: 'Test data' },
                  { id: 'team', label: 'Team' },
                  { id: 'settings', label: 'Settings' },
                ]
        }
      />
      <TabPanel id={tab}>
        {tab === 'tests' &&
          (tests.isPending ? (
            <PageLoader />
          ) : tests.isError ? (
            <ErrorNotice error={tests.error} onRetry={() => tests.refetch()} />
          ) : isPerf ? (
            <PerfTestsTab project={p} tests={tests.data} />
          ) : isSec ? (
            <SecurityTestsTab project={p} tests={tests.data} canWrite={canWrite} />
          ) : (
            <TestsTab project={p} tests={tests.data} canWrite={canWrite} canRun={canRun} />
          ))}
        {tab === 'groups' &&
          (tests.isPending ? (
            <PageLoader />
          ) : tests.isError ? (
            <ErrorNotice error={tests.error} onRetry={() => tests.refetch()} />
          ) : (
            <GroupsTab project={p} tests={tests.data} canWrite={canWrite} canRun={canRun} />
          ))}
        {tab === 'scripts' && isPerf && <ScriptsTab project={p} />}
        {tab === 'flows' && <FlowsTab project={p} canWrite={canWrite} canRun={canRun} />}
        {tab === 'automation' && <AutomationTab project={p} canWrite={canWrite} />}
        {tab === 'runs' && <RunsTab projectId={p.id} orgId={p.orgId} />}
        {tab === 'builds' && buildsSupported(p.platform) && <BuildsTab project={p} canUpload={can('build.write', orgId)} />}
        {tab === 'data' && <TestDataTab project={p} canEdit={canWrite} />}
        {tab === 'team' && <TeamTab project={p} />}
        {tab === 'settings' && <SettingsTab project={p} canEdit={can('project.update', orgId)} canDelete={can('project.delete', orgId)} />}
      </TabPanel>
    </div>
  );
}
